import React, { useEffect, useRef, useState } from "react";
import { Box, Button, CircularProgress, FormControlLabel, Switch } from "@mui/material";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import { useTranslation } from "react-i18next";
import color from "../../../Constants/colors";
import {
  deleteVoicesPostImage,
  getVoicesPostImage,
  uploadVoicesPostImage,
} from "../../../Api/voicesPosts";
import { VoicesPost } from "../../../Models/voicesPosts";
import VoicesField from "../Common/VoicesField";
import VoicesInput from "../Common/VoicesInput";

interface Props {
  post: VoicesPost | null;
  editable: boolean;
  altText: string | null;
  aiImage: boolean;
  ensureSaved: () => Promise<VoicesPost>;
  onPostChange: (post: VoicesPost) => void;
  onFieldsChange: (fields: { alt_text?: string | null; ai_image?: boolean }) => void;
  onImageUrl: (url: string | null) => void;
}

const ACCEPTED = ".jpg,.jpeg,.png,.gif";

const textButtonSx = { color: color.gray600, textTransform: "none" };

const VoicesPostImageField: React.FC<Props> = ({
  post,
  editable,
  altText,
  aiImage,
  ensureSaved,
  onPostChange,
  onFieldsChange,
  onImageUrl,
}) => {
  const { t } = useTranslation("voicesPosts");
  const input = useRef<HTMLInputElement>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    let url: string | null = null;
    if (post?.has_image) {
      getVoicesPostImage(post.id)
        .then((blob) => {
          url = URL.createObjectURL(blob);
          setImageUrl(url);
          onImageUrl(url);
        })
        .catch(() => undefined);
    } else {
      setImageUrl(null);
      onImageUrl(null);
    }
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [post?.id, post?.has_image, version]);

  const upload = async (file: File) => {
    setBusy(true);
    try {
      const saved = await ensureSaved();
      onPostChange(await uploadVoicesPostImage(saved.id, file));
      setVersion((current) => current + 1);
    } catch {
      // the snack already shows what went wrong
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!post) return;
    setBusy(true);
    try {
      onPostChange(await deleteVoicesPostImage(post.id));
      onFieldsChange({ ai_image: false });
    } catch {
      // the snack already shows what went wrong
    } finally {
      setBusy(false);
    }
  };

  return (
    <VoicesField label={t("image")} hint={t("imageHint")}>
      {imageUrl && (
        <Box
          component="img"
          src={imageUrl}
          alt={altText ?? ""}
          sx={{
            display: "block",
            maxWidth: "100%",
            maxHeight: 220,
            borderRadius: 2,
            border: `1px solid ${color.gray200}`,
            mb: 1,
          }}
        />
      )}
      {editable && (
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1 }}>
          <input
            ref={input}
            type="file"
            accept={ACCEPTED}
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = "";
              if (file) upload(file);
            }}
          />
          <Button
            variant="outlined"
            startIcon={busy ? <CircularProgress size={16} /> : <AddPhotoAlternateOutlinedIcon />}
            disabled={busy}
            onClick={() => input.current?.click()}
            sx={{
              textTransform: "none",
              color: color.orange600,
              borderColor: color.orange600,
              "&:hover": { borderColor: color.orange600, backgroundColor: color.orange50 },
            }}
          >
            {post?.has_image ? t("imageReplace") : t("imageAdd")}
          </Button>
          {post?.has_image && (
            <Button onClick={remove} disabled={busy} sx={textButtonSx}>
              {t("imageRemove")}
            </Button>
          )}
        </Box>
      )}
      {post?.has_image && (
        <>
          <VoicesInput
            placeholder={t("altTextPlaceholder")}
            disabled={!editable}
            value={altText ?? ""}
            onChange={(e) => onFieldsChange({ alt_text: e.target.value || null })}
          />
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={aiImage}
                disabled={!editable}
                onChange={(e) => onFieldsChange({ ai_image: e.target.checked })}
                sx={{
                  "& .Mui-checked": { color: `${color.orange600} !important` },
                  "& .Mui-checked + .MuiSwitch-track": {
                    backgroundColor: `${color.orange600} !important`,
                  },
                }}
              />
            }
            label={t("aiImage")}
            sx={{ mt: 0.5, "& .MuiFormControlLabel-label": { color: color.gray900, fontSize: "0.875rem" } }}
          />
        </>
      )}
    </VoicesField>
  );
};

export default VoicesPostImageField;
